import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { AggregateResponse } from '@r1-compass-library/models/reporting/aggregate-response.model';
import {
    PlayDatabaseInteraction,
    PlayError,
    PlayResult,
    PlaySalesforceInteraction,
} from '@r1-compass-library/models/reporting/reporting-data.model';
import { ReportingAggregateRequest } from '@r1-compass-library/models/reporting/reporting-request.model';
import {
    AbstractReportingSearch,
    DatabaseInteractionSearch,
    PlayErrorReportingSearch,
    ReportingSearchField,
    SalesforceInteractionSearch,
} from '@r1-compass-library/models/reporting/reporting-search.model';
import { PlayDatabaseInteractionService } from '@r1-compass-library/services/reporting/play-database-interaction.service';
import { PlayErrorService } from '@r1-compass-library/services/reporting/play-error.service';
import { PlayResultDetailsService } from '@r1-compass-library/services/reporting/play-result-details.service';
import { PlaySalesforceInteractionService } from '@r1-compass-library/services/reporting/play-salesforce-interaction.service';
import { filterNil } from 'ngxtension/filter-nil';
import { Observable, combineLatest, exhaustMap, forkJoin, map, mergeMap } from 'rxjs';

export interface ReportingDataState {
    dailyAggregates: AggregateResponse[];
    playResults: PlayResult[];
    playSalesforceInteractions: PlaySalesforceInteraction[];
    playSalesforceInteractionAggregates: { [key: string]: any }[];
    playDatabaseInteractions: PlayDatabaseInteraction[];
    playDatabaseInteractionAggregates: { [key: string]: any }[];
    playErrors: PlayError[];
    recommendationsCreated: AggregateResponse[];
    recommendationsFailed: AggregateResponse[];
    recommendationsRejected: AggregateResponse[];
    recommendationsUpdated: AggregateResponse[];
    recommendationsCreatedLast24Hours: AggregateResponse[];
    recommendationsUpdatedLast24Hours: AggregateResponse[];
}

export const initialState: ReportingDataState = {
    dailyAggregates: [],
    playResults: [],
    playSalesforceInteractions: [],
    playSalesforceInteractionAggregates: [],
    playDatabaseInteractions: [],
    playDatabaseInteractionAggregates: [],
    playErrors: [],
    recommendationsCreated: [],
    recommendationsFailed: [],
    recommendationsRejected: [],
    recommendationsUpdated: [],
    recommendationsCreatedLast24Hours: [],
    recommendationsUpdatedLast24Hours: [],
};

@Injectable({
    providedIn: 'root',
})
export class ReportingDataStore extends ComponentStore<ReportingDataState> {
    readonly #playResultDetailService = inject(PlayResultDetailsService);
    readonly #playErrorService = inject(PlayErrorService);
    readonly #playDatabaseInteractionService = inject(PlayDatabaseInteractionService);
    readonly #playSalesforceInteractionService = inject(PlaySalesforceInteractionService);

    constructor() {
        super(initialState);
    }

    playErrors$ = this.select(state => state.playErrors);
    recommendationsCreated$ = this.select(state => state.recommendationsCreated);
    recommendationsFailed$ = this.select(state => state.recommendationsFailed);
    recommendationsUpdated$ = this.select(state => state.recommendationsUpdated);
    recommendationsRejected$ = this.select(state => state.recommendationsRejected);
    databaseInteractions$ = this.select(state => state.playDatabaseInteractions);
    databaseInteractionAggregates$ = this.select(state => state.playDatabaseInteractionAggregates);
    salesforceInteractions$ = this.select(state => state.playSalesforceInteractions);
    salesforceInteractionAggregates$ = this.select(state => state.playSalesforceInteractionAggregates);

    resetState = this.updater(state => ({
        ...state,
        ...initialState,
    }));

    setRecommendationsCreated = this.updater((state, recommendationsCreated: AggregateResponse[]) => ({
        ...state,
        recommendationsCreated: recommendationsCreated,
    }));

    setRecommendationsCreatedLast24Hours = this.updater((state, recommendationsCreated: AggregateResponse[]) => ({
        ...state,
        recommendationsCreatedLast24Hours: recommendationsCreated,
    }));

    setRecommendationsFailed = this.updater((state, recommendationsFailed: AggregateResponse[]) => ({
        ...state,
        recommendationsFailed: recommendationsFailed,
    }));

    setRecommendationsRejected = this.updater((state, recommendationsRejected: AggregateResponse[]) => ({
        ...state,
        recommendationsRejected: recommendationsRejected,
    }));

    setRecommendationsUpdated = this.updater((state, recommendationsUpdated: AggregateResponse[]) => ({
        ...state,
        recommendationsUpdated: recommendationsUpdated,
    }));

    setRecommendationsUpdatedLast24Hours = this.updater((state, recommendationsUpdated: AggregateResponse[]) => ({
        ...state,
        recommendationsUpdatedLast24Hours: recommendationsUpdated,
    }));

    setDailyAggregates = this.updater((state, dailyAggregates: AggregateResponse[]) => ({
        ...state,
        dailyAggregates: dailyAggregates,
    }));

    setPlayErrors = this.updater((state, playErrors: PlayError[]) => ({
        ...state,
        playErrors: playErrors,
    }));

    setDatabaseInteractions = this.updater((state, databaseInteractions: PlayDatabaseInteraction[]) => ({
        ...state,
        playDatabaseInteractions: databaseInteractions,
    }));

    setSalesforceInteractions = this.updater((state, salesforceInteractions: PlaySalesforceInteraction[]) => ({
        ...state,
        playSalesforceInteractions: salesforceInteractions,
    }));

    setDatabaseInteractionAggregates = this.updater((state, databaseInteractions: { [key: string]: any }[]) => ({
        ...state,
        playDatabaseInteractionAggregates: databaseInteractions,
    }));

    setSalesforceInteractionAggregates = this.updater((state, salesforceInteractions: { [key: string]: any }[]) => ({
        ...state,
        playSalesforceInteractionAggregates: salesforceInteractions,
    }));

    getCountsForLast24Hours = this.effect(
        (trigger$: Observable<{ requests: ReportingAggregateRequest[]; countsType: CountsType }>) => {
            return trigger$.pipe(
                mergeMap(({ requests, countsType }) => {
                    return forkJoin(requests.map(req => this.#playResultDetailService.getAggregates(req))).pipe(
                        map(res => res.flat()),
                        tapResponse({
                            next: counts => {
                                switch (countsType) {
                                    case 'numRecordsCreated':
                                        this.setRecommendationsCreatedLast24Hours(counts);
                                        break;
                                    case 'numRecordsUpdated':
                                        this.setRecommendationsUpdatedLast24Hours(counts);
                                        break;
                                    default:
                                        break;
                                }
                            },
                            error: (err: HttpErrorResponse) => console.error(err),
                        })
                    );
                })
            );
        }
    );

    getAggregates = this.effect(
        (trigger$: Observable<{ requests: ReportingAggregateRequest[]; countsType: CountsType }>) => {
            return trigger$.pipe(
                mergeMap(({ requests, countsType }) => {
                    const serviceCalls: Observable<AggregateResponse[]>[] = [];
                    requests.forEach(req => serviceCalls.push(this.#playResultDetailService.getAggregates(req)));
                    return forkJoin(serviceCalls).pipe(
                        map(responses => this.combineResponsesOnKey(responses)),
                        tapResponse({
                            next: counts => {
                                switch (countsType) {
                                    case 'numRecordsCreated':
                                        this.setRecommendationsCreated(counts);
                                        break;
                                    case 'numRecordsFailed':
                                        this.setRecommendationsFailed(counts);
                                        break;
                                    case 'numRecordsRejected':
                                        this.setRecommendationsRejected(counts);
                                        break;
                                    case 'numRecordsUpdated':
                                        this.setRecommendationsUpdated(counts);
                                        break;
                                    default:
                                        break;
                                }
                            },
                            error: (err: HttpErrorResponse) => console.error(err),
                        })
                    );
                })
            );
        }
    );

    getErrors = this.effect((errors$: Observable<PlayErrorReportingSearch>) => {
        return errors$.pipe(
            mergeMap(errorsRequest => {
                const serviceCall: Observable<PlayError[]> =
                    this.#playErrorService.getErrorDetailsForReporting(errorsRequest);
                return serviceCall.pipe(
                    tapResponse({
                        next: errors => {
                            this.setPlayErrors(errors);
                        },
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    getInteractions = this.effect((resultId$: Observable<number>) => {
        return resultId$.pipe(
            exhaustMap(resultId => {
                const dbServiceCall: Observable<PlayDatabaseInteraction[]> = this.#playDatabaseInteractionService.find({
                    playResultId: resultId,
                } as DatabaseInteractionSearch);
                const sfServiceCall: Observable<PlaySalesforceInteraction[]> =
                    this.#playSalesforceInteractionService.find({
                        playResultId: resultId,
                    } as SalesforceInteractionSearch);
                return combineLatest([dbServiceCall.pipe(), sfServiceCall.pipe()]).pipe(
                    tapResponse({
                        next: ([dbInteractions, sfInteractions]) => {
                            this.setDatabaseInteractions(dbInteractions);
                            this.setSalesforceInteractions(sfInteractions);
                        },
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    getJoinedAggregateInteractions = this.effect((baseSearch$: Observable<AbstractReportingSearch>) => {
        return baseSearch$.pipe(
            filterNil(),
            exhaustMap(search => {
                const databaseInteractionSearch: DatabaseInteractionSearch =
                    this.generateDatabaseInteractionSearch(search);
                const salesforceInteractionSearch: SalesforceInteractionSearch =
                    this.generateSalesforceInteractionSearch(search);
                return combineLatest([
                    this.#playDatabaseInteractionService.getJoinedAggregates(databaseInteractionSearch),
                    this.#playSalesforceInteractionService.getJoinedAggregates(salesforceInteractionSearch),
                ]).pipe(
                    tapResponse({
                        next: ([databaseInteractions, salesforceInteractions]) => {
                            this.setDatabaseInteractionAggregates(databaseInteractions);
                            this.setSalesforceInteractionAggregates(salesforceInteractions);
                        },
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    private generateDatabaseInteractionSearch(search: AbstractReportingSearch): DatabaseInteractionSearch {
        const dbSearch: DatabaseInteractionSearch = structuredClone(search);
        dbSearch.status = 'Failure';
        dbSearch.fields.push(
            new ReportingSearchField('Database', undefined, 'system', 'constant'),
            new ReportingSearchField('rawException', 'play_database_interaction'),
            new ReportingSearchField('id', 'play_database_interaction', 'totalInteractions', 'count'),
            new ReportingSearchField('numRecordsSuccessful', 'play_result_detail', 'totalSucceeded', 'sum'),
            new ReportingSearchField('numRecordsFailed', 'play_result_detail', 'totalFailed', 'sum'),
            new ReportingSearchField('numRecordsRejected', 'play_result_detail', 'totalRejected', 'sum'),
            new ReportingSearchField('executionId', 'play_result'),
            new ReportingSearchField('id', 'play_result'),
            new ReportingSearchField('executionType', 'play_result'),
            new ReportingSearchField('startDate', 'play_result', 'startDate', 'min'),
            new ReportingSearchField('endDate', 'play_result', 'endDate', 'max')
        );
        dbSearch.groupByFields.push(
            new ReportingSearchField('executionId', 'play_result'),
            new ReportingSearchField('id', 'play_result'),
            new ReportingSearchField('rawException', 'play_database_interaction'),
            new ReportingSearchField('executionType', 'play_result')
        );
        return dbSearch;
    }

    private generateSalesforceInteractionSearch(search: AbstractReportingSearch): SalesforceInteractionSearch {
        const sfSearch: SalesforceInteractionSearch = structuredClone(search);
        sfSearch.fields.push(
            new ReportingSearchField('Salesforce', undefined, 'system', 'constant'),
            new ReportingSearchField('rawException', 'play_salesforce_interaction'),
            new ReportingSearchField('statusCode', 'play_salesforce_interaction'),
            new ReportingSearchField('id', 'play_salesforce_interaction', 'totalInteractions', 'count'),
            new ReportingSearchField('numRecordsSuccessful', 'play_result_detail', 'totalSucceeded'),
            new ReportingSearchField('numRecordsFailed', 'play_result_detail', 'totalFailed'),
            new ReportingSearchField('numRecordsRejected', 'play_result_detail', 'totalRejected'),
            new ReportingSearchField('executionId', 'play_result'),
            new ReportingSearchField('id', 'play_result'),
            new ReportingSearchField('executionType', 'play_result'),
            new ReportingSearchField('startDate', 'play_result', 'startDate', 'min'),
            new ReportingSearchField('endDate', 'play_result', 'endDate', 'max')
        );
        sfSearch.groupByFields.push(
            new ReportingSearchField('executionId', 'play_result'),
            new ReportingSearchField('id', 'play_result'),
            new ReportingSearchField('statusCode', 'play_salesforce_interaction'),
            new ReportingSearchField('rawException', 'play_salesforce_interaction'),
            new ReportingSearchField('executionType', 'play_result'),
            new ReportingSearchField('numRecordsSuccessful', 'play_result_detail', 'totalSucceeded'),
            new ReportingSearchField('numRecordsFailed', 'play_result_detail', 'totalFailed'),
            new ReportingSearchField('numRecordsRejected', 'play_result_detail', 'totalRejected')
        );
        return sfSearch;
    }

    /**
     * ex: Transform [ [{ key: 1, aggregate: 100 }, { key: 2, aggregate: 200 }], [{ key 1, aggregate: 300 }] ]
     *     into [{ key: 1, aggregate: 400 }, { key: 2, aggregate 200 }]
     */
    private combineResponsesOnKey(responses: AggregateResponse[][]): AggregateResponse[] {
        const flatArr = responses.flat();
        const map = new Map();
        flatArr.forEach(({ key, aggregate }) => {
            if (map.has(key)) {
                map.set(key, map.get(key) + aggregate);
            } else {
                map.set(key, aggregate);
            }
        });
        const combinedResponses = Array.from(map, ([key, aggregate]) => ({ key, aggregate })) as AggregateResponse[];
        return combinedResponses;
    }
}

export type CountsType = 'numRecordsCreated' | 'numRecordsFailed' | 'numRecordsRejected' | 'numRecordsUpdated';
